'use client';

import { FC, useState, Fragment } from 'react';
import { Dialog, Text, Icon, CardTable, CellContainer } from '@/_components';
import type { IdentityVerification, Signer } from '@/_types/models/taxReturns';
import { IdentityVerificationPill } from '@/_components/IdentityVerificationPill';
import styles from './IdentityVerificationDialog.module.css';
import { ExpandedContent } from './_components/ExpandedContent';
import { Timestamp } from '@/_components/Timestamp';

interface VerificationRow {
  id: number | string;
  name: string;
  email: string | null;
  verification: IdentityVerification;
}

interface VerificationWithUser extends IdentityVerification {
  user_id?: number;
}

export interface IdentityVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  companyName?: string;
  signers: Array<Signer>;
  verifications?: Array<VerificationWithUser>;
}

export const IdentityVerificationDialog: FC<
  IdentityVerificationDialogProps
> = ({ isOpen, onClose, companyName, signers, verifications = [] }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number | string>>(
    new Set(),
  );

  const toggleRow = (id: number | string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      prev.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const getDetailsMessage = (verification: IdentityVerification) => {
    if (verification?.error_description) return verification.error_description;
    if (verification?.status === 'verified') {
      return 'This signer has successfully passed all identity verification checks.';
    }
    return 'You may want to send a reminder to the signer to complete their tax return review.';
  };

  const verificationRows: VerificationRow[] = [
    // Rows with signers
    ...signers.map((signer) => ({
      id: signer.id,
      name: `${signer.lname}, ${signer.fname}`,
      email: signer.email,
      verification: verifications.find((v) => v.user_id === signer.id) || {
        status: 'not_started',
        error_code: null,
        error_description: null,
      },
    })),
    // Rows without signers
    ...verifications
      .filter((v) => !signers.some((s) => s.id === v.user_id))
      .map((v, i) => ({
        id: `orphaned-${i}`,
        name: '',
        email: null,
        verification: v,
      })),
  ];

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Verification Checks (ID Fraud Detection)"
      titleTag={companyName}
      isWide={true}
    >
      <Dialog.Content>
        <CardTable isLoading={false} isInline>
          <table className="sorabanTable sorabanTable--small">
            <thead>
              <tr>
                <th>
                  <Text type="small">Signer Name</Text>
                </th>
                <th>
                  <Text type="small">Email</Text>
                </th>
                <th>
                  <Text type="small">Status</Text>
                </th>
                <th>
                  <Text type="small">Started</Text>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {verificationRows.map((row, rowKey) => (
                <Fragment key={rowKey}>
                  <tr>
                    <td>
                      <CellContainer width={220}>
                        <Text type="small">{row.name}</Text>
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={200}>
                        <Text type="small">{row.email}</Text>
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={100}>
                        <IdentityVerificationPill
                          status={row.verification?.status}
                          error_code={row.verification?.error_code}
                        />
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={100}>
                        {row.verification
                          .stripe_verification_session_created_at ? (
                          <Text type="tiny" color="secondary">
                            <Timestamp
                              timestamp={
                                row.verification
                                  .stripe_verification_session_created_at
                              }
                              hideTime={true}
                              isFromNow={false}
                            />
                          </Text>
                        ) : (
                          <Text type="tiny" color="disabled">
                            —
                          </Text>
                        )}
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={32}>
                        <Icon
                          name={
                            expandedRows.has(row.id) ? 'caret-up' : 'caret-down'
                          }
                          onClick={() => toggleRow(row.id)}
                          className={styles.toggleIcon}
                          size={16}
                          color="primary"
                        />
                      </CellContainer>
                    </td>
                  </tr>
                  {expandedRows.has(row.id) && (
                    <tr>
                      <td colSpan={4}>
                        <ExpandedContent
                          status={row.verification?.status}
                          heading={
                            row.verification?.status === 'verified'
                              ? 'Verification Passed'
                              : row.verification?.status === 'failed'
                                ? 'Verification Failed'
                                : 'Verification Not Started'
                          }
                          details={getDetailsMessage(row.verification)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </CardTable>
      </Dialog.Content>
    </Dialog>
  );
};
